<template>
  <v-menu
    ref="menu"
    v-model="isMenuOpened"
    content-class="zoox-component zoox-input-date mt-2"
    offset-y
    max-width="290px"
    offset-overflow
    v-bind="$attrs"
    :return-value="value"
    :close-on-content-click="false"
  >
    <template #activator="{on, attrs}">
      <z-input
        ref="input-date"
        v-bind="attrs"
        persistent-hint
        :small="small"
        :medium="medium"
        :large="large"
        :mask="range ? null : '##/##/####'"
        :readonly="range"
        :color="inputColor || 'primary'"
        :hint="hint"
        :hide-details="true"
        :rules="rules"
        :dense="isDense"
        :clearable="clearable"
        :label="currentLabel"
        :value="inputValue"
        :disabled="disabled"
        :error-messages="errorMessages"
        :placeholder="placeholder"
        v-on="on"
        @blur="onBlur"
        @input="onInput"
        @focus="onClick"
        @click="onClick"
      >
        <template #prepend-inner>
          <z-icon
            ref="calendarIcon"
            :color="calendarColor"
            class="calendar-icon"
            v-on="on"
            @mouseover="calendarColor = 'var(--primary)'"
            @mouseout="calendarColor = null"
          >
            $calendar
          </z-icon>
        </template>
      </z-input>
    </template>

    <v-date-picker
      ref="datePicker"
      no-title
      show-adjacent-months
      scrollable
      next-icon="$keyboard_arrow_right_rounded"
      prev-icon="$keyboard_arrow_left_rounded"
      :color="color || 'primary'"
      :range="range"
      :min="min"
      :max="max"
      :value="dateModel"
      @input="onChangeDate"
    >
      <template v-if="!eager">
        <v-spacer></v-spacer>

        <z-btn
          ref="cancel"
          :color="color"
          :text="cancelBtnText"
          @click="isMenuOpened = false"
        />

        <z-btn
          ref="ok"
          v-bind="confirmBtnProps"
          primary
          dark
          :color="color"
          :text="confirmBtnText"
          @click="onConfirm"
        />
      </template>
    </v-date-picker>
  </v-menu>
</template>

<script>
import inputMixin from '../inputControlPropsMixin'

const getFormattedDate = (date = '', format = '') => {
  if (Array.isArray(date)) {
    console.warn('Incorrect value type, must use string for non-range types')
    return ''
  }
  if (date === '') return ''
  const [year, month, day] = date.split('-')
  const currentDate = new Date().setFullYear(year, month - 1, day)

  return new Intl.DateTimeFormat(format, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(currentDate)
}

/**
 * @displayName Input Date
 */
export default {
  name: 'ZInputDate',
  mixins: [inputMixin],
  props: {
    /**
     * Label do botão de cancelar
     */
    cancelText: {
      type: String,
      required: false,
    },
    /**
     * Adiciona funcionalidade de "limpar" o input
     */
    clearable: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Label do botão de confirmar
     */
    confirmText: {
      type: String,
      required: false,
    },
    /**
     * Flag para indicar que a mudança de valor é feita
     * no momento do click na data e não na confirmação
     * do botão.
     */
    eager: {
      type: Boolean,
      default: false,
    },
    /**
     * Formato de exibição da data
     *
     * @typedef {pt-BR|en-US,es-ES}
     */
    format: {
      type: String,
      default: 'pt-BR',
      validator: (value) => ['pt-BR', 'en-US', 'es-ES'].includes(value),
    },
    /**
     * Valor para o input
     */
    value: {
      type: [Array, String],
    },
    /**
     * Flag para indicar que se quer um
     * range de datas
     */
    range: {
      type: Boolean,
      default: false,
    },
    /**
     * Data mínima permitida (padrão ISO 8601 dd)
     */
    min: {
      type: String,
    },
    /**
     * Data máxima permitida (padrão ISO 8601 dd)
     */
    max: {
      type: String,
    },
    /**
     * Cor para ser usada no input
     */
    inputColor: {
      type: String,
      default: null,
    },
    /**
     * Cor utilizada no picker e nos botões
     */
    color: {
      type: String,
      default: null,
    },
    /**
     * Props para serem adicionadas ao botão de confirmação
     */
    confirmBtnProps: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Flag para indicar que o input deve ser grande (defaults: {mobile: true, desktop: false})
     */
    large: {
      type: Boolean,
      required: false,
    },
    /**
     * Flag para indicar que o input deve ser pequeno (defaults: {mobile: false, desktop: false})
     */
    small: {
      type: Boolean,
      required: false,
    },
    /**
     * Flag para indicar que o input deve ser medio sempre (defaults: {mobile: false, desktop: true})
     */
    medium: {
      type: Boolean,
      default: false,
    },
  },
  data(vm) {
    return {
      isMenuOpened: false,
      dateModel: vm.range ? [] : '',
      calendarColor: null,
      inputValue: '',
    }
  },
  computed: {
    confirmBtnText() {
      return this.confirmText || this.$t('ok')
    },
    cancelBtnText() {
      return this.cancelText || this.$t('cancel')
    },
  },
  watch: {
    value(value) {
      this.dateModel = value
      this.setInputValue(this.dateModel)
    },
    range(value) {
      if (value) {
        this.$nextTick(() => {
          this.inputValue = ''
        })
      } else if (!value) {
        this.$nextTick(() => {
          this.inputValue = ''
        })
      }
    },
  },

  mounted() {
    if (this.value) {
      this.dateModel = this.value
      this.setInputValue(this.dateModel)
    }
  },

  methods: {
    onCancel() {
      this.isMenuOpened = false
    },
    onConfirm() {
      /**
       * Evento emitido ao confirmar a data
       *
       * @property {String} value - Data selecinada
       */
      this.$emit('input', this.dateModel)
      this.setInputValue(this.dateModel)
      this.isMenuOpened = false
    },
    onChangeDate(date) {
      this.dateModel = date

      if (this.eager) {
        if (!this.range) {
          this.$emit('input', this.dateModel)
          this.setInputValue(this.dateModel)
          this.isMenuOpened = false
        } else if (this.dateModel.length === 2) {
          this.$emit('input', this.dateModel)
          this.setInputValue(this.dateModel)
          this.isMenuOpened = false
        }
      }

      this.setInputValue(this.dateModel)
    },

    setInputValue(date) {
      if (!this.range) {
        this.inputValue = getFormattedDate(date, this.format)
      } else if (date.length >= 2) {
        const [start, end] = date

        this.inputValue = `${getFormattedDate(
          start,
          this.format
        )} - ${getFormattedDate(end, this.format)}`
      }
    },

    onInput(value) {
      this.inputValue = value

      if (!value) {
        this.$emit('input', '')
        setTimeout(() => {
          this.isMenuOpened = true
        }, 10)
      } else {
        this.isMenuOpened = false
      }
    },

    onBlur() {
      if (this.range) return
      if (!this.inputValue) return

      let day, month, year

      if (this.format === 'en-US') {
        const chunk = this.inputValue.split('/')
        day = chunk[1]
        month = chunk[0]
        year = chunk[2]
      } else {
        const chunk = this.inputValue.split('/')
        day = chunk[0]
        month = chunk[1]
        year = chunk[2]
      }

      const date = new Date(`${year}-${month}-${day}`)

      if (
        !day ||
        !month ||
        !year ||
        day.length < 2 ||
        month.length < 2 ||
        year.length < 4 ||
        isNaN(date)
      ) {
        this.inputValue = ''
        this.dateModel = this.range ? [] : ''
        this.$emit('invalid:date')
        this.$emit('input', this.dateModel)
        return
      }

      this.dateModel = `${year}-${month}-${day}`

      this.$emit('input', this.dateModel)
    },

    onClick() {
      setTimeout(() => {
        this.isMenuOpened = true
        window.dispatchEvent(new Event('resize'))
        this.$emit('click')
      }, 10)
    },
  },
  i18n: {
    en: {
      ok: 'Ok',
      cancel: 'Cancel',
    },
    es: {
      ok: 'Ok',
      cancel: 'Cancelar',
    },
    pt: {
      ok: 'Ok',
      cancel: 'Cancelar',
    },
    fr: {
      ok: 'Ok',
      cancel: 'Annuler',
    },
    tr: {
      ok: 'Tamam',
      cancel: 'İptal',
    },
  },
}
</script>

<style lang="scss" scoped>
.zoox-input-date {
  box-shadow: var(--zoox-card-shadow);

  .v-massages {
    min-height: 0px !important;
  }
  .v-text-field__details {
    min-height: 0px !important;
  }
}

.calendar-icon {
  margin-top: 2px;
}
</style>
